import { onboardingImageCandidates } from "./onboardingImageUrl";

/** Known intrinsic pixel sizes for onboarding PNG illustrations. */
export const ONBOARDING_ILLUSTRATION_DIMENSIONS: Readonly<
  Record<number, { readonly width: number; readonly height: number }>
> = {
  1: { width: 1024, height: 1536 },
  2: { width: 1024, height: 1536 },
  3: { width: 1024, height: 1536 },
  4: { width: 1024, height: 1536 },
  10: { width: 1024, height: 1536 },
};

const preloadedImages = new Map<string, HTMLImageElement>();

let preloadPromise: Promise<void> | null = null;

export function getOnboardingIllustrationDimensions(
  step: number,
): { width: number; height: number } | undefined {
  return ONBOARDING_ILLUSTRATION_DIMENSIONS[step];
}

/** All illustration URLs needed during the active onboarding flow (steps 1–4 + setup). */
export function getAllOnboardingImageUrls(): string[] {
  const urls = new Set<string>();

  for (let step = 1; step <= 4; step++) {
    for (const url of onboardingImageCandidates(step)) {
      urls.add(url);
    }
  }

  for (const url of onboardingImageCandidates(10)) {
    urls.add(url);
  }

  return [...urls];
}

/** Fetches, decodes, and caches a single image. Shared by the eager preloader and the render path. */
export async function preloadSingleImage(url: string): Promise<HTMLImageElement | undefined> {
  const cached = preloadedImages.get(url);
  if (cached) {
    return cached;
  }

  const img = new Image();

  await new Promise<void>((resolve) => {
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });

  if (!img.complete || img.naturalWidth === 0) {
    return undefined;
  }

  try {
    await img.decode();
  } catch {
    // decode() may reject on unsupported or corrupt images; cache best-effort.
  }

  preloadedImages.set(url, img);
  return img;
}

/** Eagerly fetch and decode all onboarding illustrations for the active flow. */
export async function preloadOnboardingImages(): Promise<void> {
  await Promise.all(getAllOnboardingImageUrls().map(preloadSingleImage));
}

/** Idempotent preload kickoff shared by bootstrap and React hooks. */
export function ensureOnboardingImagesPreloaded(): Promise<void> {
  if (!preloadPromise) {
    preloadPromise = preloadOnboardingImages();
  }
  return preloadPromise;
}

export function areOnboardingImagesReady(): boolean {
  return getAllOnboardingImageUrls().every((url) => preloadedImages.has(url));
}

export function getPreloadedOnboardingImage(url: string): HTMLImageElement | undefined {
  return preloadedImages.get(url);
}

/** @internal Resets preload cache — for unit tests only. */
export function resetOnboardingImagePreloadForTests(): void {
  preloadPromise = null;
  preloadedImages.clear();
}
